import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, SkipForward, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, inr, formatApiErrorDetail } from "@/lib/api";
import { Panel } from "@/components/crm/common";

const fail = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong");
const sel = "h-9 rounded-lg border border-zinc-200 bg-white px-3 text-sm";

function Promise({ i, onSaved }) {
  const p = i.payment_promise;
  const [f, setF] = useState({ date: p?.date || "", note: p?.note || "" });
  const save = (date) => api.post(`/sales/payment-reminders/${i.id}/promise`, { date, note: f.note }).then(() => { toast.success(date ? `Reminders paused until ${date}` : "Promise cleared, reminders resume"); onSaved(); }).catch(fail);
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs" data-testid={`promise-${i.invoice_number}`}>
      {p && <span data-testid={`promise-badge-${i.invoice_number}`} className="rounded-full bg-emerald-50 px-2 py-0.5 font-bold text-emerald-700">Promised {p.date} · paused{p.note ? ` · ${p.note}` : ""}</span>}
      <input data-testid={`promise-date-${i.invoice_number}`} type="date" className="h-7 rounded-md border border-zinc-200 px-2" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
      <input data-testid={`promise-note-${i.invoice_number}`} placeholder="Client said…" className="h-7 w-40 rounded-md border border-zinc-200 px-2" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
      <button data-testid={`promise-save-${i.invoice_number}`} disabled={!f.date} onClick={() => save(f.date)} className="font-bold text-[#8B7356] disabled:opacity-40">Save promise</button>
      {p && <button data-testid={`promise-clear-${i.invoice_number}`} onClick={() => save("")} className="text-zinc-500">Clear</button>}
    </div>
  );
}

export function PaymentReminders() {
  const [d, setD] = useState(null);
  const load = () => api.get("/sales/payment-reminders").then((r) => setD(r.data)).catch((e) => setD({ error: e.response?.data?.detail }));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  if (d.error) return <Panel testid="payrem-no-access"><p className="text-sm text-zinc-500">{d.error}</p></Panel>;
  const s = d.settings;
  const save = (patch) => api.put("/sales/payment-reminders/settings", { ...s, ...patch }).then(() => { toast.success("Reminder rules saved"); load(); }).catch(fail);
  const act = (iid, step, a) => api.post(`/sales/payment-reminders/${iid}/${step}/${a}`).then((r) => { toast.success(`Step ${step}: ${r.data.status}`); load(); }).catch(fail);
  const run = () => api.post("/sales/payment-reminders/run").then((r) => { toast.success(r.data.message); setTimeout(load, 15000); }).catch(fail);
  return (
    <div className="space-y-4" data-testid="payment-reminders">
      <Panel testid="payrem-settings" title="Automatic 3-step client payment reminders">
        <p className="mb-3 text-xs text-zinc-500">Step 1 gentle → Step 2 firm → Step 3 final notice. At Step 3, Accounts, the CEO/Founder approvers and the owner get an alert to call the client. Runs daily at 10:00 IST.</p>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-2"><input data-testid="payrem-enabled" type="checkbox" checked={s.enabled} onChange={(e) => save({ enabled: e.target.checked })} />On</label>
          <select data-testid="payrem-schedule" className={sel} value={s.schedule} onChange={(e) => save({ schedule: e.target.value })}>
            <option value="0,7,15">Due date · 7 days late · 15 days late</option><option value="7,15,30">7 · 15 · 30 days late</option>
          </select>
          <select data-testid="payrem-mode" className={sel} value={s.mode} onChange={(e) => save({ mode: e.target.value })}>
            <option value="auto">Email the client automatically</option><option value="approve">Accounts approves each email first</option>
          </select>
          <Button size="sm" variant="outline" onClick={run} data-testid="payrem-run"><Play className="mr-1 h-4 w-4" />Run now</Button>
        </div>
      </Panel>
      <Panel testid="payrem-list" title="Reminder status by invoice">
        {!d.invoices.length && <p className="text-sm text-zinc-500">No unpaid invoices due in the next 7 days.</p>}
        <div className="divide-y divide-zinc-100">
          {d.invoices.map((i) => (
            <div key={i.id} data-testid={`payrem-row-${i.invoice_number}`} className="flex flex-col gap-2 py-3 md:flex-row md:items-center md:justify-between">
              <div><div className="font-bold">{i.invoice_number} · {i.client}</div><div className="text-xs text-zinc-500">{i.project_name} · due {i.due_date} · balance {inr(i.balance)} · {i.client_email || "no client email"}</div>
                <Promise i={i} onSaved={load} /></div>
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3].map((n) => {
                  const r = i.payment_reminders?.[n];
                  return (
                    <div key={n} title={d.steps[n]} className="rounded-lg bg-[#F3ECE2] px-2.5 py-1 text-[11px]" data-testid={`payrem-${i.invoice_number}-step-${n}`}>
                      <b>Step {n}</b>: <span className={r?.status === "Sent" ? "text-emerald-700" : r?.status?.startsWith("Failed") || r?.status === "No client email" ? "text-rose-700" : r?.status === "Awaiting Accounts approval" ? "text-amber-700" : ""}>{r ? r.status : "Not yet"}</span>
                      {r?.status === "Awaiting Accounts approval" && <span className="ml-1 inline-flex gap-1">
                        <button data-testid={`payrem-send-${i.invoice_number}-${n}`} onClick={() => act(i.id, n, "send")} className="font-bold text-[#8B7356]"><Send className="inline h-3 w-3" /> Send</button>
                        <button data-testid={`payrem-skip-${i.invoice_number}-${n}`} onClick={() => act(i.id, n, "skip")} className="text-zinc-500"><SkipForward className="inline h-3 w-3" /> Skip</button></span>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
